import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import {
  HotspotSessionRecord,
  HotspotSessionRecordListQuery,
  HotspotSessionRecordListResponse
} from '../models/hotspot-session-record.model';

interface ApiListResponse {
  data?: any[];
  meta?: Partial<HotspotSessionRecordListResponse['meta']>;
}

interface ApiDetailResponse {
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class HotspotSessionRecordService {
  private apiUrl = `${environment.apiUrl}/hotspot/session-records`;

  constructor(private http: HttpClient) {}

  getSessionRecords(query: HotspotSessionRecordListQuery): Observable<HotspotSessionRecordListResponse> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('perPage', query.perPage);

    if (query.provider?.trim()) params = params.set('provider', query.provider.trim());
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    if (query.passengerProfileID) params = params.set('passengerProfileID', query.passengerProfileID);
    if (query.organisationID) params = params.set('organisationID', query.organisationID);
    if (query.vehicleID) params = params.set('vehicleID', query.vehicleID);
    if (query.nasIdentifier?.trim()) params = params.set('nasIdentifier', query.nasIdentifier.trim());
    if (query.active !== undefined && query.active !== null) params = params.set('active', query.active);
    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);

    return this.http.get<ApiListResponse>(this.apiUrl, { params }).pipe(
      map(response => ({
        data: (response.data || []).map(row => this.mapSessionRecord(row)),
        meta: {
          page: Number(response.meta?.page || query.page),
          perPage: Number(response.meta?.perPage || query.perPage),
          total: Number(response.meta?.total || 0),
          totalPages: Number(response.meta?.totalPages || 1)
        }
      }))
    );
  }

  getSessionRecord(id: number): Observable<HotspotSessionRecord> {
    return this.http.get<ApiDetailResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => this.mapSessionRecord(response.data || response))
    );
  }

  private mapSessionRecord(row: any): HotspotSessionRecord {
    return {
      hotspotSessionRecordID: Number(row.hotspotSessionRecordID ?? row.hotspotSessionRecordId ?? row.id),
      provider: row.provider ?? 'Powerlynx',
      externalID: row.externalID ?? row.externalId,
      sessionID: row.sessionID ?? row.sessionId,
      passengerProfileID: row.passengerProfileID ?? row.passengerProfileId ?? null,
      passengerDisplayName: row.passengerDisplayName,
      passengerPhone: row.passengerPhone,
      phoneNumber: row.phoneNumber,
      nasIdentifier: row.nasIdentifier,
      organisationID: row.organisationID ?? row.organisationId ?? null,
      organisationName: row.organisationName,
      vehicleID: row.vehicleID ?? row.vehicleId ?? null,
      vehicleRegistration: row.vehicleRegistration,
      vehicleLabel: row.vehicleLabel,
      startDateTime: row.startDateTime,
      endDateTime: row.endDateTime,
      active: Boolean(row.active),
      dataIn: row.dataIn ?? null,
      dataOut: row.dataOut ?? null,
      dataPlan: row.dataPlan,
      terminationCause: row.terminationCause
    };
  }
}
